/*
 * The size finder's logic: reading a Kiwi chart, checking it, estimating the body and choosing the size.
 * No page code here, so the same file runs on the product page (window.MMSizeFinder) and in Node's tests
 * (scripts/theme/size-finder.test.mjs).
 *
 * The rule (the Zara audit of 2026-09-26, docs/greek-store.md): a size label stands for a body. The size is
 * the smallest label on this product's chart whose body value covers hers, bust for tops and hips for
 * trousers. The body values come from the chart's own body table when Kiwi has one («Μετρήσεις σώματος»),
 * otherwise from the maker's standard (snippets/mm-body-chart.liquid). The garment table never decides;
 * it has to show every label really fits its body, and it explains the pick.
 */
(function (root) {
  "use strict";

  /** Every size label this store uses, smallest first. */
  var LADDER = ["XXS", "XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL", "5XL", "6XL"];
  var ALIAS = { XXL: "2XL", XXXL: "3XL", "2X": "2XL", "3X": "3XL" };

  /** A garment must beat the body its label is for by at least this much, or nobody that size can wear it. */
  var MIN_ROOM_CM = 2;

  /** Fit moves the body value a little before the lookup, so it can change the size only near a boundary. */
  var FIT_SHIFT_CM = { fitted: -2, normal: 0, loose: 2 };

  function label(s) {
    var t = String(s || "").trim().toUpperCase().replace(/\s+/g, "");
    return ALIAS[t] || t;
  }

  function number(s) {
    var t = String(s == null ? "" : s).trim().replace(",", ".");
    if (!/^\d+(\.\d+)?$/.test(t)) return NaN;
    return Number(t);
  }

  /** What a header measures. Flat widths are marked so they can be doubled. */
  function measureOf(header) {
    var h = String(header).toLowerCase();
    if (/(πλάτος|φάρδος)\s*στήθους|half\s*chest|chest\s*width|pit\s*to\s*pit|ancho\s*(de\s*)?pecho/.test(h)) return { key: "chest", flat: true };
    if (/περίμετρος\s*στήθους|στήθος|chest|bust|seno|busto|petto|pecho/.test(h)) return { key: "chest" };
    if (/γοφ|περιφέρεια\s*λεκάνης|hip|fianchi|cadera/.test(h)) return { key: "hips" };
    if (/μέση|waist|vita|cintura/.test(h)) return { key: "waist" };
    return null;
  }

  function isBodyTable(headers) {
    return headers.some(function (h) { return /σώματος|body|cuerpo|corporal/i.test(h); });
  }

  /**
   * One Kiwi sizing (as getSizingChart returns it) into { name, id, updatedAt, labels, garment, body }.
   * garment and body map label to { chest, hips, waist } in cm. Throws on anything it cannot read for sure.
   */
  function parseKiwiChart(sizing) {
    if (!sizing || !sizing.tables) throw new Error("no tables");
    var out = { name: sizing.name, id: sizing.id, updatedAt: sizing.updatedAt, labels: [], garment: null, body: null };
    Object.keys(sizing.tables).forEach(function (key) {
      var t = sizing.tables[key];
      if (!t || t.hide || !t.data || t.data.length < 2) return;
      var rows = t.data.map(function (r) { return r.map(function (c) { return c.value; }); });
      if (t.direction && t.direction !== "col") throw new Error("table " + key + " is laid out by row; only column tables are read");
      var headers = rows[0];
      var cols = headers.map(measureOf);
      if (!cols.some(Boolean)) return;
      var table = {};
      var order = [];
      rows.slice(1).forEach(function (r) {
        var l = label(r[0]);
        if (LADDER.indexOf(l) < 0) throw new Error("unknown size label " + JSON.stringify(r[0]));
        var m = {};
        cols.forEach(function (c, i) {
          if (!c) return;
          var v = number(r[i]);
          if (!(v > 0)) throw new Error(l + ": " + JSON.stringify(headers[i]) + " is not a number (" + JSON.stringify(r[i]) + ")");
          m[c.key] = c.flat ? v * 2 : v;
        });
        table[l] = m;
        order.push(l);
      });
      if (isBodyTable(headers)) {
        if (out.body) throw new Error("two body tables");
        out.body = table;
      } else {
        if (out.garment) throw new Error("two garment tables");
        out.garment = table;
        out.labels = order.slice().sort(function (a, b) { return LADDER.indexOf(a) - LADDER.indexOf(b); });
        if (out.labels.join() !== order.join()) throw new Error("sizes are not in order: " + order.join(", "));
      }
    });
    if (!out.garment) throw new Error("no garment table with a chest, hips or waist column");
    return out;
  }

  /** Tops are chosen by chest; a chart with hips and no chest is trousers or a skirt, chosen by hips. */
  function decidingMeasure(chart) {
    var first = chart.garment[chart.labels[0]];
    if (first.chest != null) return "chest";
    if (first.hips != null) return "hips";
    return null;
  }

  /** The standard body chart (mm-body-chart.liquid) into label -> { chest, hips, waist }. */
  function standardBody(bodyChart) {
    var out = {};
    bodyChart.sizes.forEach(function (s) { out[label(s.label)] = { chest: s.bust, hips: s.hips, waist: s.waist }; });
    return out;
  }

  /**
   * Whether a chart may power the size finder, and why not. options.elasticWaist skips the waist on
   * trousers (João confirms it per chart).
   */
  function checkChart(chart, bodyChart, options) {
    options = options || {};
    var body = chart.body || standardBody(bodyChart);
    var measure = decidingMeasure(chart);
    var reasons = [];
    var flags = [];
    var rooms = [];
    if (!measure) reasons.push("no chest or hips column to choose by");
    chart.labels.forEach(function (l, i) {
      if (!measure) return;
      var g = chart.garment[l][measure];
      var b = body[l] && body[l][measure];
      if (b == null) { reasons.push(l + " has no body value"); return; }
      if (i > 0 && !(g > chart.garment[chart.labels[i - 1]][measure])) reasons.push(measure + " does not grow from " + chart.labels[i - 1] + " to " + l);
      var room = Math.round((g - b) * 10) / 10;
      rooms.push({ label: l, garment: g, body: b, room: room });
      if (room < MIN_ROOM_CM) reasons.push(l + " measures " + g + ", not enough for the " + b + " body it is labelled for" + (room <= 0 ? "; this supplier sizes small" : ""));
    });
    if (measure === "hips" && chart.garment[chart.labels[0]].waist != null && !options.elasticWaist) {
      var tight = chart.labels.every(function (l) { return body[l] && chart.garment[l].waist < body[l].waist; });
      if (tight) flags.push("the waist is smaller than the body at every size, so it is probably elastic: confirm before trusting");
      else chart.labels.forEach(function (l) {
        if (body[l] && chart.garment[l].waist - body[l].waist < MIN_ROOM_CM) reasons.push(l + " waist " + chart.garment[l].waist + " is below the " + body[l].waist + " body waist");
      });
    }
    var tightest = rooms.slice().sort(function (a, b) { return a.room - b.room; })[0];
    return {
      pass: reasons.length === 0 && flags.length === 0,
      measure: measure,
      bodySource: chart.body ? "chart" : "standard",
      rooms: rooms,
      tightest: tightest ? tightest.label : null,
      reasons: reasons,
      flags: flags,
    };
  }

  /**
   * Bust in cm from height (cm) and weight (kg). Calibrated on Zara's recommender (2026-09-26): its size
   * switch weights at 150, 155, 165 and 185 cm, read through Zara's body chart, fit
   *   bust = -15.33693 + 1.53947 * BMI + 0.41246 * height
   * within 1.6 kg of every measured switch. Togo & Stark's formula was 1 to 6 cm high at those points.
   */
  function estimateBust(heightCm, weightKg) {
    if (!(heightCm >= 130 && heightCm <= 210) || !(weightKg >= 35 && weightKg <= 200)) return null;
    var bmi = weightKg / Math.pow(heightCm / 100, 2);
    return -15.33693 + 1.53947 * bmi + 0.41246 * heightCm;
  }

  /**
   * The size for one body on one checked chart.
   * input: { chart, bodyChart, value (bust or hips in cm), fit: fitted|normal|loose, sold: [labels on sale] }
   * Returns { label, body, garment, room } or { none: "above" } when even the largest label is too small.
   */
  function recommend(input) {
    var chart = input.chart;
    var body = chart.body || standardBody(input.bodyChart);
    var measure = decidingMeasure(chart);
    var v = input.value + (FIT_SHIFT_CM[input.fit || "normal"] || 0);
    var sold = input.sold ? input.sold.map(label) : chart.labels;
    var candidates = chart.labels.filter(function (l) { return sold.indexOf(l) >= 0 && body[l]; });
    for (var i = 0; i < candidates.length; i++) {
      var l = candidates[i];
      if (body[l][measure] >= v) {
        var g = chart.garment[l][measure];
        return { label: l, measure: measure, body: body[l][measure], garment: g, room: Math.round((g - input.value) * 10) / 10 };
      }
    }
    return { none: "above", largest: candidates[candidates.length - 1] || null };
  }

  var api = {
    LADDER: LADDER, MIN_ROOM_CM: MIN_ROOM_CM, FIT_SHIFT_CM: FIT_SHIFT_CM,
    label: label, number: number, parseKiwiChart: parseKiwiChart, decidingMeasure: decidingMeasure,
    standardBody: standardBody, checkChart: checkChart, estimateBust: estimateBust, recommend: recommend,
  };
  if (typeof module === "object" && module.exports) module.exports = api;
  else root.MMSizeFinder = api;
})(typeof window !== "undefined" ? window : this);
